export type T_ClientTraceValue = boolean | null | number | string | undefined;
export type T_ClientTraceAttributes = Record<string, T_ClientTraceValue>;

const MAX_CLIENT_TRACE_ATTRIBUTE_COUNT = 32;
const MAX_CLIENT_TRACE_ATTRIBUTE_KEY_LENGTH = 64;
const MAX_CLIENT_TRACE_ATTRIBUTE_VALUE_LENGTH = 256;
const MAX_CLIENT_TRACE_CONSOLE_ARGUMENTS = 8;
const MAX_CLIENT_TRACE_CONSOLE_MESSAGE_LENGTH = 256;
const SAFE_CONSOLE_LABEL_PATTERN = /^\[[A-Za-z][A-Za-z0-9 .:_-]{0,63}\]/;
const PRIVATE_ATTRIBUTE_KEY_PARTS = new Set([
  "amount",
  "auth",
  "balance",
  "content",
  "cookie",
  "credential",
  "cuid",
  "id",
  "markdown",
  "portfolio",
  "prompt",
  "secret",
  "text",
  "token",
  "value",
]);

type T_CreateClientTraceAttributesArgs = {
  attributes?: T_ClientTraceAttributes;
  clientElapsedMs: number;
  sequence: number;
  traceSession: string;
};

function isPrivateAttributeKey(key: string): boolean {
  const parts = key
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1_$2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  return parts.some((part) => PRIVATE_ATTRIBUTE_KEY_PARTS.has(part));
}

function normalizeAttributeValue(
  value: Exclude<T_ClientTraceValue, undefined>,
): string {
  const normalized = value === null ? "null" : String(value);
  return normalized.slice(0, MAX_CLIENT_TRACE_ATTRIBUTE_VALUE_LENGTH);
}

function describeConsoleArgument(argument: unknown): string {
  if (argument === null) return "[null]";
  if (Array.isArray(argument)) return "[array omitted]";

  switch (typeof argument) {
    case "bigint":
      return "[bigint omitted]";
    case "boolean":
      return "[boolean omitted]";
    case "function":
      return "[function omitted]";
    case "number":
      return "[number omitted]";
    case "object":
      return "[object omitted]";
    case "string":
      return "[string omitted]";
    case "symbol":
      return "[symbol omitted]";
    case "undefined":
      return "[undefined]";
  }

  return "[unknown omitted]";
}

/**
 * Console capture is useful on physical browsers, but arbitrary console values
 * are not a safe telemetry contract. Keep only a bracketed subsystem label and
 * describe every remaining argument by type; structured state belongs in
 * logClientTrace events instead.
 */
export function serializeClientTraceConsoleArgs(args: unknown[]): string {
  const firstArgument = args[0];
  const safeLabel =
    typeof firstArgument === "string"
      ? (firstArgument.match(SAFE_CONSOLE_LABEL_PATTERN)?.[0] ??
        "[console message redacted]")
      : "[console message redacted]";
  const descriptions = args
    .slice(1, MAX_CLIENT_TRACE_CONSOLE_ARGUMENTS)
    .map(describeConsoleArgument);

  return [safeLabel, ...descriptions]
    .join(" ")
    .slice(0, MAX_CLIENT_TRACE_CONSOLE_MESSAGE_LENGTH);
}

/**
 * Faro event attributes are strings. This boundary also drops identifiers and
 * content-bearing fields, and bounds every event before it enters the SDK.
 */
export function createClientTraceAttributes({
  attributes,
  clientElapsedMs,
  sequence,
  traceSession,
}: T_CreateClientTraceAttributesArgs): Record<string, string> {
  const normalized: Record<string, string> = {
    client_elapsed_ms: clientElapsedMs.toFixed(3),
    sequence: String(sequence),
    trace_session: traceSession,
  };

  let acceptedAttributeCount = 0;
  for (const [key, value] of Object.entries(attributes ?? {})) {
    if (
      value === undefined ||
      acceptedAttributeCount >= MAX_CLIENT_TRACE_ATTRIBUTE_COUNT ||
      key.length > MAX_CLIENT_TRACE_ATTRIBUTE_KEY_LENGTH ||
      isPrivateAttributeKey(key)
    ) {
      continue;
    }
    normalized[key] = normalizeAttributeValue(value);
    acceptedAttributeCount += 1;
  }

  return normalized;
}
